import React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import { PasswordRecoveryRequestDTO } from '@/core/api/dtos/request/auth/passwordRecovery'
import { useTranslation } from '@/core/i18n/useTranslation'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/atoms/Alert'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { H2 } from '@/shared/components/atoms/H2'
import { Input } from '@/shared/components/atoms/Input'
import { Link } from '@/shared/components/atoms/Link'
import { Spinner } from '@/shared/components/atoms/Spinner'

import { OnbardingLayout } from '../OnbardingLayout'

interface ForgotPasswordUIProps {
	isLoading: boolean
	error: string | null
	success: boolean
	form: UseFormReturn<
		PasswordRecoveryRequestDTO,
		any,
		PasswordRecoveryRequestDTO
	>
	onFinish: (values: PasswordRecoveryRequestDTO) => void
}

export const ForgotPasswordUI: React.FC<ForgotPasswordUIProps> = ({
	isLoading,
	error,
	success,
	form,
	onFinish,
}) => {
	// Initialize translations with auth namespace
	const { t } = useTranslation('auth')
	const { t: tCommon } = useTranslation('common')

	return (
		<OnbardingLayout hideImageOnStep={false}>
			<div className="space-y-4">
				<H2 className="flex-center">{t('forgotPassword.title')}</H2>

				<p className="text-center mb-4 text-secondaryPink">
					{t('forgotPassword.instructions')}
				</p>

				<Spinner spinning={isLoading}>
					{error && (
						<Alert variant="error" className="mb-4">
							<AlertTitle>{tCommon('common.error')}</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					{success && (
						<Alert variant="success" className="mb-4">
							<AlertTitle>{t('common.success')}</AlertTitle>
							<AlertDescription>{t('forgotPassword.success')}</AlertDescription>
						</Alert>
					)}

					<form onSubmit={form.handleSubmit(onFinish)} noValidate>
						<div>
							<label className="text-xs mb-2 block">
								{t('forgotPassword.email')}
							</label>
							<Controller
								control={form.control}
								name="email"
								render={({ field, fieldState: { error } }) => (
									<div>
										<Input
											placeholder={t('forgotPassword.email').toLowerCase()}
											{...field}
										/>
										{error && <ErrorMessage error={error.message ?? ''} />}
									</div>
								)}
							/>
						</div>

						<div className="flex justify-center gap-2 mt-3">
							<div className="mb-0 w-full">
								<Button
									className="h-10 px-8 py-3 cursor-pointer w-full text-sm font-medium rounded-full"
									type="submit"
									disabled={isLoading || !form.formState.isValid}
								>
									{t('forgotPassword.submit')}
								</Button>
							</div>
						</div>
					</form>
				</Spinner>

				<div className="mt-4 text-center text-sm">
					<Link to="/login">{t('forgotPassword.backToLogin')}</Link>
				</div>
			</div>
		</OnbardingLayout>
	)
}
